import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createDraftImage, fetchDefaultPrompt, generateImage, listImages, deleteImage as deleteImageApi } from '../api/images';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:4500/api').replace(/\/api$/, '');

const Workspace = ({ token }) => {
  const [images, setImages] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [loadingImages, setLoadingImages] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoDraftCreated, setAutoDraftCreated] = useState(false);
  const [moveCursorToEnd, setMoveCursorToEnd] = useState(false);
  const promptRef = useRef(null);

  const selectedImage = useMemo(() => {
    const current = images.find((img) => img.id === selectedId);
    return current || images[0] || null;
  }, [images, selectedId]);

  const activeId = selectedImage?.id ?? null;

  const previewUrl = selectedImage?.filePath ? `${API_BASE}${selectedImage.filePath}` : null;

  const loadImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const { images: payload } = await listImages(token);
      setImages(payload);
      if (payload.length) {
        setSelectedId(payload[0].id);
      }
    } catch (err) {
      setError(err.message || 'Не удалось загрузить изображения');
    } finally {
      setLoadingImages(false);
      setInitialLoadComplete(true);
    }
  }, [token]);

  const loadDefaultPrompt = useCallback(async () => {
    try {
      const { prompt: defaultPrompt } = await fetchDefaultPrompt(token);
      setPrompt(defaultPrompt);
      setMoveCursorToEnd(true);
    } catch (err) {
      setPrompt('Опишите сцену для генерации изображения.');
      setMoveCursorToEnd(true);
      setError(err.message || 'Не удалось загрузить промпт');
    }
  }, [token]);

  const createDraftAndFocus = useCallback(async () => {
    const { prompt: freshPrompt } = await fetchDefaultPrompt(token);
    const { image } = await createDraftImage(token, { prompt: freshPrompt });
    setImages((prev) => [image, ...prev]);
    setSelectedId(image.id);
    setPrompt(freshPrompt);
    setMoveCursorToEnd(true);
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadImages();
    loadDefaultPrompt();
  }, [token, loadImages, loadDefaultPrompt]);

  useEffect(() => {
    if (!token || autoDraftCreated || !initialLoadComplete) {
      return;
    }

    (async () => {
      try {
        await createDraftAndFocus();
      } catch (err) {
        setError(err.message || 'Не удалось создать новое задание');
      } finally {
        setAutoDraftCreated(true);
      }
    })();
  }, [token, autoDraftCreated, initialLoadComplete, createDraftAndFocus]);

  useEffect(() => {
    if (selectedImage) {
      setPrompt(selectedImage.prompt);
      setMoveCursorToEnd(true);
    }
  }, [selectedImage]);

  useEffect(() => {
    if (moveCursorToEnd && promptRef.current) {
      const len = promptRef.current.value.length;
      promptRef.current.focus();
      promptRef.current.setSelectionRange(len, len);
      setMoveCursorToEnd(false);
    }
  }, [moveCursorToEnd]);

  const handleGenerate = async (event) => {
    event.preventDefault();
    if (!title.trim() || !prompt.trim()) {
      setError('Введите название и промпт перед генерацией');
      return;
    }

    setGenerating(true);
    setError(null);
    try {
      const { image } = await generateImage(token, { title: title.trim(), prompt: prompt.trim() });
      setImages((prev) => [image, ...prev]);
      setSelectedId(image.id);
      setTitle('');
    } catch (err) {
      setError(err.message || 'Не удалось сгенерировать изображение');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!previewUrl) {
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedImage?.title || 'image'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Не удалось сохранить изображение');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Удалить задание?')) {
      return;
    }
    setError(null);
    try {
      await deleteImageApi(token, imageId);
      setImages((prev) => prev.filter((img) => img.id !== imageId));
      if (selectedId === imageId) {
        setSelectedId(null);
      }
    } catch (err) {
      setError(err.message || 'Не удалось удалить задание');
    }
  };

  const handleOpenTab = () => {
    const nextUrl = window.location.href.includes('?')
      ? `${window.location.href}&tab=${Date.now()}`
      : `${window.location.href}?tab=${Date.now()}`;
    window.open(nextUrl, '_blank');
  };

  const handleNewTask = async () => {
    setGenerating(true);
    setError(null);
    try {
      await createDraftAndFocus();
    } catch (err) {
      setError(err.message || 'Не удалось создать новое задание');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="workspace">
      <aside className="workspace__sidebar">
        <div className="workspace__sidebar-header">
          <button type="button" onClick={handleOpenTab}>
            Открыть новую вкладку
          </button>
        </div>
        <h2>Мои изображения</h2>
        {loadingImages ? (
          <p className="workspace__muted">Загрузка...</p>
        ) : images.length === 0 ? (
          <p className="workspace__muted">Ещё нет сгенерированных изображений</p>
        ) : (
          <ul className="workspace__list">
            {images.map((img) => (
              <li key={img.id} className={img.id === activeId ? 'active' : ''}>
                <button
                  type="button"
                  className="workspace__delete"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(img.id);
                  }}
                  aria-label="Удалить"
                >
                  ✕
                </button>
                <div onClick={() => setSelectedId(img.id)} role="button" tabIndex={0}>
                  <strong>{img.title}</strong>
                  <span>{new Date(img.createdAt).toLocaleString()}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <section className="workspace__editor">
        <form className="workspace__form" onSubmit={handleGenerate}>
          <label>
            Промпт
            <textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={6}
              placeholder="Нужна обложка на курс: ..."
              disabled={generating}
            />
          </label>

          <label>
            Название изображения
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Например: «Космический город»"
              disabled={generating}
            />
          </label>

          {error && <div className="workspace__error">{error}</div>}

          <div className="workspace__actions">
            <button type="submit" disabled={generating}>
              {generating ? 'Генерация...' : 'Сгенерировать'}
            </button>
            <button
              type="button"
              className="secondary"
              onClick={handleSave}
              disabled={saving || !previewUrl}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button type="button" className="secondary" onClick={handleNewTask} disabled={generating}>
              Новое задание
            </button>
          </div>
        </form>

        <div className="workspace__preview">
          <h3>Сгенерированное изображение</h3>
          {previewUrl ? (
            <img src={previewUrl} alt={selectedImage?.title} />
          ) : (
            <p className="workspace__muted">Выберите изображение слева или создайте новое</p>
          )}
        </div>
      </section>
    </div>
  );
};

Workspace.propTypes = {
  token: PropTypes.string.isRequired,
};

export default Workspace;






